import { useEffect, useState } from 'react';
import { readTrust } from '@/lib/mfa-trust';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { startMemberSession } from '@/lib/agencyMember';
import { parkChallenge } from '@/Login';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

/**
 * LinkedIn sign-in landing page. LinkedIn bounces here with ?code&state; the
 * code goes to /api/auth/linkedin/callback, and what comes back is handled
 * exactly as a Google sign-in is (oauth-google-callback): a session, a second
 * factor to ask, an agency member, or a signup that only needs the company.
 */
export default function OAuthLinkedInCallback() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('exchanging'); // exchanging | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const errorParam = params.get('error_description') || params.get('error');
    if (errorParam) { setPhase('error'); setMessage(`LinkedIn did not sign you in: ${errorParam}`); return; }
    if (!code) { setPhase('error'); setMessage('Missing authorization code in callback URL.'); return; }
    axios
      .post(`${API_URL}/api/auth/linkedin/callback`, { code, state, mfa_trust: readTrust() })
      .then(({ data }) => {
        if (data.needs_workspace) {
          // LinkedIn proved who they are; only the company name is left.
          setPhase('success');
          setMessage('Signed in with LinkedIn. One more thing\u2026');
          sessionStorage.setItem('gsignup', JSON.stringify({ ticket: data.ticket, email: data.email, name: data.name }));
          setTimeout(() => navigate('/signup?google=1'), 600);
          return;
        }
        // EXACTLY what Login.js stores, through the same helper for agency
        // members - a second idea of what a session is would log people in
        // to a half-built one.
        // LinkedIn was the first factor. The second is asked for on the login
        // page, which owns those screens.
        if (data.mfa_required || data.mfa_setup_required) {
          parkChallenge(data);
          navigate('/');
          return;
        }
        if (data.agency_session) {
          startMemberSession(data.token, data.member);
          setPhase('success');
          setMessage('Signed in. Choose a workspace\u2026');
          setTimeout(() => window.location.assign('/app'), 600);
          return;
        }
        localStorage.setItem('token', data.token);
        localStorage.setItem('userId', data.user.id);
        localStorage.setItem('userName', data.user.name);
        setPhase('success');
        setMessage('Signed in. Taking you in\u2026');
        setTimeout(() => window.location.assign('/app'), 600);
      })
      .catch((err) => {
        setPhase('error');
        setMessage(err.response?.data?.error || err.message || 'LinkedIn sign-in failed.');
      });
  }, [navigate]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-4 p-8 text-center">
          {phase === 'exchanging' && (
            <>
              <Loader2 className="mx-auto size-8 animate-spin text-primary" />
              <h2 className="text-base font-semibold">Signing in with LinkedIn</h2>
            </>
          )}
          {phase === 'success' && (
            <>
              <CheckCircle2 className="mx-auto size-8 text-success" />
              <h2 className="text-base font-semibold">Signed in</h2>
              <p className="text-sm text-muted-foreground">{message}</p>
            </>
          )}
          {phase === 'error' && (
            <>
              <AlertCircle className="mx-auto size-8 text-destructive" />
              <h2 className="text-base font-semibold">LinkedIn did not sign you in</h2>
              <p className="text-sm text-muted-foreground">{message}</p>
              <Button size="sm" onClick={() => window.location.assign('/app')}>Back to sign in</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
